import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Database,
  Play,
  RefreshCw,
  Server,
  Table2,
  TrendingUp,
  XCircle,
} from "lucide-react";
import { dataflowApi } from "@/services/api/dataflow";
import { apiClient } from "@/services/api/client";
import type { TaskChain } from "@/types/dataflow";

interface Metrics {
  running: number;
  pending: number;
  failed: number;
  completed: number;
  queued: number;
  totalToday: number;
  successRate: number;
}

interface QueueInstance {
  id: number;
  batch_id?: number;
  template_id: number;
  name: string;
  status: string;
  task_type: string;
  priority: number;
  queued_at?: string;
  started_at?: string;
  worker_id?: string;
  trigger_mode?: string;
}

interface QueueStatusLite {
  pending_instances?: QueueInstance[];
  running_instances?: QueueInstance[];
}

interface DataOverview {
  stock_total_count?: number;
  listed_stock_count?: number;
  latest_trade_date?: string;
  latest_price_count?: number;
  latest_indicator_count?: number;
  daily_price_total_rows?: number;
  daily_indicator_total_rows?: number;
  daily_price_date_range?: string;
}

interface FreshnessRow {
  table_name: string;
  latest_trade_date: string;
  lag_days: number;
  status: string;
}

function useDataViewMetrics(taskChains: TaskChain[] = [], queue: QueueStatusLite = {}) {
  return useMemo<Metrics>(() => {
    const running = queue.running_instances?.length || 0;
    const pending = queue.pending_instances?.length || 0;

    let failed = 0;
    let completed = 0;
    let totalToday = taskChains.length;
    let totalCompleted = 0;

    taskChains.forEach((batch) => {
      failed += batch.failed_instances || 0;
      completed += batch.completed_instances || 0;
      if (batch.status === "COMPLETED") {
        totalCompleted++;
      }
    });

    const successRate = totalToday > 0 ? (totalCompleted / totalToday) * 100 : 100;

    return {
      running,
      pending,
      failed,
      completed,
      queued: pending + running,
      totalToday,
      successRate,
    };
  }, [taskChains, queue]);
}

function formatTimeAgo(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diff < 60) return "刚刚";
  if (diff < 3600) return `${Math.floor(diff / 60)}分钟前`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}小时前`;
  return `${Math.floor(diff / 86400)}天前`;
}

function formatNumber(value: number | undefined): string {
  if (value == null || !Number.isFinite(value)) return "--";
  return value.toLocaleString("zh-CN");
}

function formatTradeDate(value: string | undefined): string {
  const raw = String(value || "").trim();
  if (/^\d{8}$/.test(raw)) {
    return `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`;
  }
  return raw || "--";
}

function StatusToneClass(value: string | undefined): string {
  const status = String(value || "").toUpperCase();
  if (["OK", "COMPLETED", "ACTIVE", "SUCCESS"].includes(status)) {
    return "text-emerald-400";
  }
  if (["WARN", "PARTIAL", "RUNNING", "QUEUED", "ACK", "NEW"].includes(status)) {
    return "text-amber-400";
  }
  if (["ERROR", "FAILED", "STALE", "MISSING", "EMPTY"].includes(status)) {
    return "text-rose-400";
  }
  return "text-slate-400";
}

export function DataView() {
  const [selectedDate, setSelectedDate] = useState<string>(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  });

  // DataFlow task-chain metrics
  const { data: taskChains = [], isLoading: taskChainsLoading, refetch: refetchTaskChains } = useQuery({
    queryKey: ["dataflow", "batches", selectedDate],
    queryFn: () => dataflowApi.getTaskChains({ date: selectedDate, limit: 50 }),
    refetchInterval: 10000,
  });

  const { data: queue, isLoading: queueLoading, refetch: refetchQueue } = useQuery({
    queryKey: ["dataflow", "queue"],
    queryFn: () => dataflowApi.getQueue(),
    refetchInterval: 5000,
  });

  const { data: timeline, isLoading: timelineLoading } = useQuery({
    queryKey: ["dataflow", "timeline", { limit: 10 }],
    queryFn: () => dataflowApi.getTimeline({ limit: 10 }),
    refetchInterval: 10000,
  });

  // Data table metrics from ops dashboard
  const { data: dataStatus, isLoading: dataStatusLoading } = useQuery({
    queryKey: ["ops", "data-status"],
    queryFn: () => apiClient.getOpsDataStatus({ days: 7, includeToday: true }),
    refetchInterval: 30000,
  });

  const { data: freshness, isLoading: freshnessLoading } = useQuery({
    queryKey: ["ops", "freshness"],
    queryFn: () => apiClient.getOpsFreshness(),
    refetchInterval: 30000,
  });

  const metrics = useDataViewMetrics(taskChains, queue);
  const overview: DataOverview = dataStatus?.overview || {};

  const handleRefresh = () => {
    refetchTaskChains();
    refetchQueue();
  };

  const recentTaskChains = useMemo(() => {
    return [...taskChains]
      .sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime())
      .slice(0, 5);
  }, [taskChains]);

  return (
    <div className="h-full overflow-auto p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-slate-100">数据视图</h2>
          <p className="text-[11px] text-[var(--muted)]">DataFlow 执行监控与数据状态</p>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="h-8 rounded border border-[var(--border)] bg-[#0c0f14] px-2 text-xs text-slate-100"
          />
          <button
            onClick={handleRefresh}
            disabled={taskChainsLoading || queueLoading}
            className="flex h-8 items-center gap-1 rounded border border-[var(--border)] bg-[#0c0f14] px-2 text-xs text-[var(--muted)] hover:text-slate-100 disabled:opacity-50"
          >
            <RefreshCw className={`size-3.5 ${taskChainsLoading || queueLoading ? "animate-spin" : ""}`} />
            刷新
          </button>
        </div>
      </div>

      {/* Top Row: Data Overview Cards */}
      <div className="grid grid-cols-5 gap-3">
        <div className="rounded border border-[var(--border)] bg-[#0f1115] p-3">
          <div className="flex items-center gap-2 text-[10px] text-[var(--muted)] uppercase">
            <Database className="size-3" />
            股票总数
          </div>
          <div className="mt-1 text-lg font-semibold text-slate-100">
            {dataStatusLoading ? "--" : formatNumber(overview.stock_total_count)}
          </div>
          <div className="text-[10px] text-emerald-400">
            上市中: {formatNumber(overview.listed_stock_count)}
          </div>
        </div>

        <div className="rounded border border-[var(--border)] bg-[#0f1115] p-3">
          <div className="flex items-center gap-2 text-[10px] text-[var(--muted)] uppercase">
            <TrendingUp className="size-3" />
            最新交易日
          </div>
          <div className="mt-1 text-lg font-semibold text-slate-100">
            {dataStatusLoading ? "--" : formatTradeDate(overview.latest_trade_date)}
          </div>
          <div className="text-[10px] text-slate-400">
            price: {formatNumber(overview.latest_price_count)} · indicator: {formatNumber(overview.latest_indicator_count)}
          </div>
        </div>

        <div className="rounded border border-[var(--border)] bg-[#0f1115] p-3">
          <div className="flex items-center gap-2 text-[10px] text-[var(--muted)] uppercase">
            <Table2 className="size-3" />
            日K数据行数
          </div>
          <div className="mt-1 text-lg font-semibold text-slate-100">
            {dataStatusLoading ? "--" : formatNumber(overview.daily_price_total_rows)}
          </div>
          <div className="text-[10px] text-slate-400 truncate" title={overview.daily_price_date_range}>
            {overview.daily_price_date_range || "--"}
          </div>
        </div>

        <div className="rounded border border-[var(--border)] bg-[#0f1115] p-3">
          <div className="flex items-center gap-2 text-[10px] text-[var(--muted)] uppercase">
            <Table2 className="size-3" />
            指标数据行数
          </div>
          <div className="mt-1 text-lg font-semibold text-slate-100">
            {dataStatusLoading ? "--" : formatNumber(overview.daily_indicator_total_rows)}
          </div>
          <div className="text-[10px] text-slate-400">累计行数</div>
        </div>

        <div className="rounded border border-[var(--border)] bg-[#0f1115] p-3">
          <div className="flex items-center gap-2 text-[10px] text-[var(--muted)] uppercase">
            <CheckCircle2 className="size-3" />
            任务成功率
          </div>
          <div className={`mt-1 text-lg font-semibold ${metrics.successRate >= 90 ? "text-emerald-400" : metrics.successRate >= 70 ? "text-amber-400" : "text-rose-400"}`}>
            {taskChainsLoading ? "--" : `${metrics.successRate.toFixed(0)}%`}
          </div>
          <div className="mt-1 h-1 rounded-full bg-[#1e293b] overflow-hidden">
            <div
              className={`h-full rounded-full ${metrics.successRate >= 90 ? "bg-emerald-500" : metrics.successRate >= 70 ? "bg-amber-500" : "bg-rose-500"}`}
              style={{ width: `${metrics.successRate}%` }}
            />
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-[1fr_360px] gap-4">
        {/* Left Column */}
        <div className="space-y-4">
          {/* Data Freshness Matrix */}
          <div className="rounded border border-[var(--border)] bg-[#0f1115]">
            <div className="flex items-center justify-between border-b border-[var(--border)] px-3 py-2">
              <div className="flex items-center gap-2">
                <Clock className="size-4 text-[var(--muted)]" />
                <span className="text-xs font-semibold text-slate-200">数据新鲜度矩阵</span>
              </div>
              <span className="text-[10px] text-[var(--muted)]">
                {freshnessLoading ? "加载中..." : `${freshness?.rows?.length || 0} 张表`}
              </span>
            </div>
            <div className="max-h-[200px] overflow-auto">
              <table className="w-full border-collapse text-[11px]">
                <thead className="sticky top-0 z-10 bg-[#141920]">
                  <tr>
                    <th className="border-b border-[var(--border)] px-2 py-1.5 text-left font-medium text-slate-300">表名</th>
                    <th className="border-b border-[var(--border)] px-2 py-1.5 text-left font-medium text-slate-300">最新数据</th>
                    <th className="border-b border-[var(--border)] px-2 py-1.5 text-left font-medium text-slate-300">滞后</th>
                    <th className="border-b border-[var(--border)] px-2 py-1.5 text-left font-medium text-slate-300">状态</th>
                  </tr>
                </thead>
                <tbody>
                  {freshness?.rows?.map((row) => (
                    <tr key={row.table_name} className="border-b border-[var(--border)]/60 hover:bg-white/5">
                      <td className="px-2 py-1.5 text-slate-200">{row.table_name}</td>
                      <td className="px-2 py-1.5 text-slate-300">{formatTradeDate(row.latest_trade_date)}</td>
                      <td className="px-2 py-1.5 text-slate-300">
                        {row.lag_days == null ? "--" : `${row.lag_days} 天`}
                      </td>
                      <td className={`px-2 py-1.5 font-medium ${StatusToneClass(row.status)}`}>
                        {row.status}
                      </td>
                    </tr>
                  ))}
                  {!freshness?.rows?.length && (
                    <tr>
                      <td colSpan={4} className="px-2 py-6 text-center text-[var(--muted)]">
                        {freshnessLoading ? "加载中..." : "暂无新鲜度数据"}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Recent Task Chains - Simplified */}
          <div className="rounded border border-[var(--border)] bg-[#0f1115]">
            <div className="flex items-center justify-between border-b border-[var(--border)] px-3 py-2">
              <div className="flex items-center gap-2">
                <Activity className="size-4 text-[var(--muted)]" />
                <span className="text-xs font-semibold text-slate-200">最近任务链</span>
              </div>
              <span className="text-[10px] text-[var(--muted)]">今日: {metrics.totalToday}</span>
            </div>
            <div className="p-3 space-y-2">
              {recentTaskChains.map((batch) => (
                <div key={batch.id} className="flex items-center justify-between text-[11px]">
                  <div className="flex items-center gap-2">
                    <div
                      className={`size-2 rounded-full ${
                        batch.status === "COMPLETED"
                          ? "bg-emerald-500"
                          : batch.status === "FAILED"
                          ? "bg-rose-500"
                          : batch.status === "RUNNING"
                          ? "bg-amber-500 animate-pulse"
                          : "bg-slate-500"
                      }`}
                    />
                    <span className="text-slate-200 truncate max-w-[200px]">{batch.name}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-slate-400">
                      {batch.completed_instances}/{batch.total_instances}
                    </span>
                    <span className="text-slate-500">
                      {batch.created_at ? formatTimeAgo(batch.created_at) : "--"}
                    </span>
                  </div>
                </div>
              ))}
              {recentTaskChains.length === 0 && (
                <div className="text-[var(--muted)] text-center py-2">
                  {taskChainsLoading ? "加载中..." : "暂无任务链数据"}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-4">
          {/* Task Metrics Cards */}
          <div className="grid grid-cols-2 gap-2">
            <MetricCard icon={Play} label="运行中" value={metrics.running} color="amber" loading={queueLoading} />
            <MetricCard icon={Clock} label="待处理" value={metrics.pending} color="blue" loading={queueLoading} />
            <MetricCard icon={CheckCircle2} label="已完成" value={metrics.completed} color="emerald" loading={taskChainsLoading} />
            <MetricCard icon={XCircle} label="失败" value={metrics.failed} color="rose" loading={taskChainsLoading} />
          </div>

          {/* Worker Slots */}
          <div className="rounded border border-[var(--border)] bg-[#0f1115]">
            <div className="flex items-center justify-between border-b border-[var(--border)] px-3 py-2">
              <div className="flex items-center gap-2">
                <Server className="size-4 text-[var(--muted)]" />
                <span className="text-xs font-semibold text-slate-200">执行队列</span>
              </div>
              <span className="text-[10px] text-[var(--muted)]">
                {queue?.slots?.filter((s) => s.status === "running").length || 0}/{queue?.slots?.length || 0}
              </span>
            </div>
            <div className="p-3">
              <div className="grid grid-cols-4 gap-1">
                {queue?.slots?.map((slot) => (
                  <div
                    key={slot.id}
                    className={`h-6 rounded flex items-center justify-center text-[10px] ${
                      slot.status === "running"
                        ? "bg-amber-500/20 text-amber-400 border border-amber-500/30"
                        : slot.status === "error"
                        ? "bg-rose-500/20 text-rose-400 border border-rose-500/30"
                        : "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30"
                    }`}
                    title={slot.name || `Slot ${slot.id}`}
                  >
                    {slot.id}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Recent Events */}
          <div className="rounded border border-[var(--border)] bg-[#0f1115]">
            <div className="flex items-center justify-between border-b border-[var(--border)] px-3 py-2">
              <div className="flex items-center gap-2">
                <Activity className="size-4 text-[var(--muted)]" />
                <span className="text-xs font-semibold text-slate-200">最近事件</span>
              </div>
            </div>
            <div className="p-3 space-y-2 max-h-[180px] overflow-auto">
              {timeline?.events?.map((event) => (
                <div key={event.id} className="flex items-start gap-2 text-[11px]">
                  <div
                    className={`mt-0.5 size-2 rounded-full shrink-0 ${
                      event.type === "instance.completed"
                        ? "bg-emerald-500"
                        : event.type === "instance.failed"
                        ? "bg-rose-500"
                        : "bg-amber-500"
                    }`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-slate-200 truncate">
                      {event.type === "instance.completed"
                        ? "完成"
                        : event.type === "instance.failed"
                        ? "失败"
                        : event.type === "instance.started"
                        ? "开始"
                        : "取消"}
                      <span className="text-slate-400 ml-1">#{event.instance_id}</span>
                    </div>
                    <div className="text-[10px] text-slate-500">{formatTimeAgo(event.timestamp)}</div>
                  </div>
                </div>
              ))}
              {!timeline?.events?.length && (
                <div className="text-[11px] text-[var(--muted)] text-center py-2">
                  {timelineLoading ? "加载中..." : "暂无事件"}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Alerts */}
      {(metrics.failed > 0 || metrics.successRate < 70) && (
        <div className="rounded border border-rose-500/30 bg-rose-500/10 p-3">
          <div className="flex items-center gap-2 text-rose-300">
            <AlertTriangle className="size-4" />
            <span className="text-xs font-medium">
              {metrics.failed > 0
                ? `今日有 ${metrics.failed} 个任务链失败，请关注`
                : "成功率低于 70%，请关注任务执行情况"}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function MetricCard({
  icon: Icon,
  label,
  value,
  color,
  loading,
}: {
  icon: typeof Activity;
  label: string;
  value: number;
  color: "amber" | "blue" | "emerald" | "rose";
  loading: boolean;
}) {
  const colorStyles = {
    amber: "bg-amber-500/10 text-amber-400 border-amber-500/20",
    blue: "bg-blue-500/10 text-blue-400 border-blue-500/20",
    emerald: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    rose: "bg-rose-500/10 text-rose-400 border-rose-500/20",
  };

  return (
    <div className={`rounded border p-2 ${colorStyles[color]}`}>
      <div className="flex items-center justify-between">
        <Icon className="size-3 opacity-70" />
        {loading && <RefreshCw className="size-3 animate-spin opacity-50" />}
      </div>
      <div className="mt-1 text-xl font-bold">{loading ? "--" : value}</div>
      <div className="text-[10px] opacity-70">{label}</div>
    </div>
  );
}
